import { useEffect, useState } from "react";
import { createProgram, deleteProgram, listPrograms, updateProgram, listStaff, listProgramsByCoachAdmin } from "../../../apis/programsApi";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  Alert,
  Snackbar,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
} from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PersonIcon from "@mui/icons-material/Person";
import RefreshIcon from "@mui/icons-material/Refresh";
import { ImageUploadWithPreview } from "../../../components/admin/programs/ImageUploadWithPreview";

const ManagePrograms = () => {
  const [programs, setPrograms] = useState<any[]>([]);
  const [form, setForm] = useState<any>({
    title: "",
    description: "",
    location: "",
    price: 0,
    isActive: true,
    imageFile: undefined,
  });
  const [editingProgram, setEditingProgram] = useState<any | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });
  const [statusTab, setStatusTab] = useState(0);
  const [staff, setStaff] = useState<Array<{ id: string; email: string; name?: string; role: string; firstName?: string; lastName?: string }>>([]);
  
  // Get user type for role-based restrictions
  const userType = localStorage.getItem("userType");
  const canEdit = ["admin", "employee", "employee_admin", "coach_admin"].includes(userType || "");

  const refresh = async () => {
    let data
    if (['admin', 'employee', 'employee_admin'].includes(userType || "")) {
      data = await listPrograms();
    }
    else {
      data = await listProgramsByCoachAdmin(localStorage.getItem("userId") || "");
    }
    setPrograms(data);
  };

  useEffect(() => {
    refresh();
    (async () => {
      try {
        const s = await listStaff(["coach"]);
        setStaff(s);
      } catch {}
    })();
  }, []);

  const onChange = (e: any, isEdit = false) => {
    const { name, value } = e.target;
    const processedValue = name === "isActive" ? value === "true" : value;
    if (isEdit) {
      setEditingProgram({ ...editingProgram, [name]: processedValue });
    } else {
      setForm({ ...form, [name]: processedValue });
    }
  };

  const onCreate = async () => {
    try {
      await createProgram({ ...form, price: Number(form.price) });
      setForm({
        title: "",
        description: "",
        location: "",
        price: 0,
        isActive: true,
        imageFile: undefined,
      });
      setCreateDialogOpen(false);
      await refresh();
      setSnackbar({ open: true, message: "Program created successfully!", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to create program", severity: "error" });
    }
  };

  const onUpdate = async () => {
    try {
      await updateProgram(editingProgram.id, { ...editingProgram, price: Number(editingProgram.price) });
      setEditingProgram(null);
      await refresh();
      setSnackbar({ open: true, message: "Program updated successfully!", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to update program", severity: "error" });
    }
  };

  const onDelete = async () => {
    if (programToDelete) {
      try {
        await deleteProgram(programToDelete);
        await refresh();
        setSnackbar({ open: true, message: "Program deleted successfully!", severity: "success" });
      } catch (error) {
        setSnackbar({ open: true, message: "Failed to delete program, There are sessions or enrollments associated with this program", severity: "error" });
      }
      setDeleteDialogOpen(false);
      setProgramToDelete(null);
    }
  };

  const handleDeleteClick = (id: string) => {
    setProgramToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleStatusTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setStatusTab(newValue);
  };

  const activePrograms = programs.filter((p) => Boolean(p.isActive));
  const inactivePrograms = programs.filter((p) => !p.isActive);
  const displayedPrograms = statusTab === 0 ? activePrograms : inactivePrograms;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack direction={{ xs: "column", sm: "column", md: "row" }} justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Manage Programs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage your training programs
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "column", md: "row" }} spacing={2}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<RefreshIcon />}
            onClick={refresh}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Refresh
          </Button>
          {canEdit && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Create Program
            </Button>
          )}
        </Stack>
      </Stack>

      {programs.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={statusTab}
            onChange={handleStatusTabChange}
            aria-label="program status tabs"
          >
            <Tab label={`Active (${activePrograms.length})`} />
            <Tab label={`Inactive (${inactivePrograms.length})`} />
          </Tabs>
        </Box>
      )}

      {programs.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center", borderRadius: 3, boxShadow: 6, border: "1px solid #e0e0e0" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No programs yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get started by creating your first program
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Your First Program
          </Button>
        </Paper>
      ) : displayedPrograms.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center", borderRadius: 3, boxShadow: 6, border: "1px solid #e0e0e0" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {statusTab === 0 ? "No active programs" : "No inactive programs"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {statusTab === 0
              ? "Switch to the Inactive tab to view deactivated programs."
              : "Switch to the Active tab to view active programs."}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {displayedPrograms.map((p) => (
            <Grid key={p.id} item xs={12} sm={6} md={4} lg={3}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  transition: "all 0.3s ease",
                  border: "1px solid #e0e0e0",
                  boxShadow: 6,
                  "&:hover": {
                    boxShadow: 6,
                    transform: "translateY(-4px)"
                  }
                }}
              >
                {p.imageUrl && (
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: { xs: 120, sm: 140, md: 160 },
                      overflow: 'hidden',
                      borderRadius: '12px 12px 0 0',
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img 
                      src={p.imageUrl} 
                      alt={p.title}
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease-in-out'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 30%)',
                        pointerEvents: 'none'
                      }}
                    />
                  </Box>
                )}
                
                {/* Fallback for no image */}
                {!p.imageUrl && (
                  <Box
                    sx={{
                      width: '100%',
                      height: { xs: 120, sm: 140, md: 160 },
                      backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '12px 12px 0 0',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      fontWeight={700} 
                      color="white"
                      sx={{ 
                        textAlign: 'center',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                        zIndex: 1
                      }}
                    >
                      {p.title}
                    </Typography>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(118, 75, 162, 0.8) 100%)',
                        pointerEvents: 'none'
                      }}
                    />
                  </Box>
                )}

                <CardContent sx={{ flex: 1, p: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                      <Typography variant="h6" fontWeight={700} sx={{ flex: 1, pr: 1 }}>
                        {p.title}
                      </Typography>
                      <Chip
                        size="small"
                        label={p.isActive ? "Active" : "Inactive"}
                        color={p.isActive ? "success" : "default"}
                        variant={p.isActive ? "filled" : "outlined"}
                      />
                    </Stack>

                    {p.location && (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LocationOnIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {p.location}
                        </Typography>
                      </Stack>
                    )}

                    <Box sx={{ py: 1 }}>
                      <Typography variant="h5" fontWeight={700} color="primary">
                        ${p.price ?? 0}
                      </Typography>
                    </Box>

                    {p.programCoach && (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          Coach: {`${p.programCoach.firstName} ${p.programCoach.lastName}` || p.programCoach.email}
                        </Typography>
                      </Stack>
                    )}

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "60px"
                      }}
                    >
                      {p.description || "No description available"}
                    </Typography>
                  </Stack>
                </CardContent>

                <Divider />

                <Stack direction="row" spacing={1} sx={{ p: 1.5 }} justifyContent="space-between" alignItems="center">
                  {canEdit && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(p.isActive)}
                          onChange={async () => {
                            try {
                              await updateProgram(p.id, { isActive: !p.isActive });
                              await refresh();
                              setSnackbar({
                                open: true,
                                message: `Program ${p.isActive ? "deactivated" : "activated"} successfully!`,
                                severity: "success",
                              });
                            } catch {
                              setSnackbar({
                                open: true,
                                message: "Failed to update program status",
                                severity: "error",
                              });
                            }
                          }}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" color={p.isActive ? "success.main" : "error.main"}>
                          {p.isActive ? "Active" : "Inactive"}
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                  )}
                  {canEdit && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => setEditingProgram({ ...p })}
                        sx={{ borderRadius: 2 }}
                      >
                        Edit
                      </Button>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(p.id)}
                        sx={{ borderRadius: 2 }}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Stack>
                  )}
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Dialog */}
      {canEdit && (
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              Create New Program
            </Typography>
            <IconButton onClick={() => setCreateDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Program Title"
              name="title"
              value={form.title}
              onChange={onChange}
              placeholder="e.g., Frostbite Bootcamp"
              required
            />
            <TextField
              select
              fullWidth
              label="Status"
              name="isActive"
              value={String(form.isActive)}
              onChange={(e) => onChange(e, false)}
            >
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </TextField>
            <ImageUploadWithPreview
              imageFile={form.imageFile}
              existingImageUrl={form.imageUrl}
              onImageChange={(file) => setForm({ ...form, imageFile: file })}
              label="Program Image"
            />
            <TextField
              select
              fullWidth
              label="Program Coach (optional)"
              name="programCoachId"
              value={(form as any).programCoachId || ""}
              onChange={(e) => onChange(e, false)}
            >
              {/* <MenuItem value="">None</MenuItem> */}
              {staff.map((u) => (
                <MenuItem key={u.id} value={u.id}>{`${u.firstName} ${u.lastName}` || u.email}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Price"
              name="price"
              type="number"
              value={form.price}
              onChange={onChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
              required
            />
            <TextField
              fullWidth
              label="Location"
              name="location"
              value={form.location}
              required
              onChange={onChange}
              placeholder="e.g., Halifax, NS, CAN"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              name="description"
              value={form.description}
              required
              onChange={onChange}
              placeholder="Describe your program..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onCreate}
            startIcon={<AddIcon />}
            disabled={!form.title}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Create Program
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Edit Dialog */}
      {canEdit && (
      <Dialog
        open={!!editingProgram}
        onClose={() => setEditingProgram(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              Edit Program
            </Typography>
            <IconButton onClick={() => setEditingProgram(null)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {editingProgram && (
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Program Title"
                name="title"
                value={editingProgram.title ?? ""}
                onChange={(e) => onChange(e, true)}
                required
              />
              <TextField
                select
                fullWidth
                label="Status"
                name="isActive"
                value={String((editingProgram as any).isActive ?? true)}
                onChange={(e) => onChange(e, true)}
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </TextField>
              <ImageUploadWithPreview
                imageFile={editingProgram.imageFile}
                existingImageUrl={editingProgram.imageUrl}
                onImageChange={(file) => setEditingProgram({ ...editingProgram, imageFile: file })}
                label="Program Image"
              />
              <TextField
                fullWidth
                label="Price"
                name="price"
                type="number"
                value={editingProgram.price ?? 0}
                onChange={(e) => onChange(e, true)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
                required
              />
              <TextField
                select
                fullWidth
                label="Program Coach (optional)"
                name="programCoachId"
                value={(editingProgram as any).programCoachId || editingProgram.programCoach?.id || ""}
                onChange={(e) => onChange(e, true)}
              >
                <MenuItem value="">None</MenuItem>
                {staff.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{`${u.firstName} ${u.lastName}` || u.email}</MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={editingProgram.location ?? ""}
                onChange={(e) => onChange(e, true)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                name="description"
                value={editingProgram.description ?? ""}
                onChange={(e) => onChange(e, true)}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setEditingProgram(null)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onUpdate}
            startIcon={<SaveIcon />}
            disabled={!editingProgram?.title}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {canEdit && (
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>
            Delete Program?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this program? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onDelete}
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManagePrograms;